import { useState } from "react";
import { useScrollAnimation, useStaggerAnimation } from "../../../hooks/useScrollAnimation";

const services = [
  {
    title: "Solar Installation",
    subtitle: "Premium Solar Panels",
    image:
      "https://public.readdy.ai/ai/img_res/cb77efb8dbfb5a3efc53ce3cb5711f0a.jpg",
    icon: "ri-sun-line",
    features: [
      "Up to 70% bill reduction",
      "Premium tier-1 panels",
      "25-year performance warranty",
      "Professional roof mounting",
    ],
    badge: "",
  },
  {
    title: "HVAC Systems",
    subtitle: "Heat Pumps & AC Units",
    image:
      "https://readdy.ai/api/search-image?query=Professional%20photograph%20of%20a%20modern%20high-efficiency%20white%20HVAC%20air%20conditioning%20unit%20installed%20outside%20a%20beautiful%20Florida%20home%2C%20clean%20concrete%20pad%2C%20neat%20copper%20piping%20and%20electrical%20connections%2C%20well-maintained%20tropical%20garden%20surroundings%2C%20bright%20natural%20daylight%2C%20sharp%20focus%20on%20the%20unit%20with%20soft%20bokeh%20background%2C%20commercial%20product%20photography%20style%20with%20warm%20tones&width=600&height=700&seq=svc-hvac-2025&orientation=portrait",
    icon: "ri-temp-cold-line",
    features: [
      "Energy-efficient heat pumps",
      "High SEER AC systems",
      "Professional installation",
      "Lifetime warranty options",
    ],
    badge: "MOST POPULAR",
  },
  {
    title: "Attic Insulation",
    subtitle: "R-30 Premium Insulation",
    image:
      "https://readdy.ai/api/search-image?query=Professional%20photograph%20of%20a%20technician%20in%20clean%20white%20protective%20suit%20installing%20premium%20blown-in%20attic%20insulation%20in%20a%20residential%20home%20attic%20space%2C%20bright%20LED%20work%20lights%20illuminating%20the%20fluffy%20white%20insulation%20material%2C%20wooden%20roof%20trusses%20visible%2C%20clean%20and%20organized%20work%20environment%2C%20high%20detail%20commercial%20photography%20with%20warm%20ambient%20lighting%20and%20professional%20composition&width=600&height=700&seq=svc-insulation-2025&orientation=portrait",
    icon: "ri-home-4-line",
    features: [
      "Reduces heat transfer",
      "Lowers cooling costs",
      "Improves home comfort",
      "Eco-friendly materials",
    ],
    badge: "",
  },
  {
    title: "Solar Attic Fans",
    subtitle: "Heat Extraction System",
    image:
      "https://readdy.ai/api/search-image?query=Professional%20photograph%20of%20a%20modern%20circular%20solar-powered%20attic%20ventilation%20fan%20installed%20on%20a%20clean%20shingled%20residential%20rooftop%2C%20small%20integrated%20solar%20panel%20on%20the%20fan%20unit%20gleaming%20in%20bright%20sunlight%2C%20clear%20blue%20Florida%20sky%20background%2C%20neat%20professional%20installation%20with%20flashing%20around%20the%20base%2C%20sharp%20detail%20commercial%20photography%20with%20warm%20natural%20lighting%20and%20vivid%20colors&width=600&height=700&seq=svc-atticfan-2025&orientation=portrait",
    icon: "ri-windy-line",
    features: [
      "Solar-powered operation",
      "Removes hot attic air",
      "Extends roof lifespan",
      "Zero operating costs",
    ],
    badge: "",
  },
];

export default function Services() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: cardsRef, getItemStyle } = useStaggerAnimation(services.length);

  return (
    <section id="services" className="py-16 lg:py-24 bg-brand-cream">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div
          ref={headerRef}
          className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-10 lg:mb-16 gap-4 lg:gap-6"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div className="max-w-2xl">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight">
              Complete Energy Solutions
              <br />
              For Florida Homes
            </h2>
          </div>
          <p className="text-base lg:text-lg text-gray-600 max-w-md">
            We provide comprehensive energy efficiency solutions tailored to
            Florida&#39;s unique climate, helping you save money while staying
            comfortable year-round.
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {services.map((service, index) => (
            <div
              key={service.title}
              className="bg-white rounded-3xl overflow-hidden group cursor-pointer transition-all duration-500 hover:shadow-2xl relative"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={getItemStyle(index)}
            >
              {service.badge && (
                <div className="absolute top-4 right-4 z-10 bg-brand-red text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                  {service.badge}
                </div>
              )}

              <div className="relative w-full h-[200px] sm:h-[260px] overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                <div
                  className={`absolute bottom-4 left-4 w-12 h-12 flex items-center justify-center rounded-xl backdrop-blur-md transition-all duration-300 ${
                    hoveredIndex === index
                      ? "bg-brand-red scale-110"
                      : "bg-white/20"
                  }`}
                >
                  <i
                    className={`${service.icon} text-2xl text-white`}
                  ></i>
                </div>
              </div>

              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                  {service.title}
                </h3>
                <p className="text-sm text-gray-500 mb-4">{service.subtitle}</p>

                <ul className="space-y-2.5 mb-6">
                  {service.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2 text-sm text-gray-700"
                    >
                      <i className="ri-check-line text-brand-red text-base mt-0.5"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 bg-brand-navy text-white px-5 py-2.5 rounded-full font-semibold hover:bg-brand-navy-dark transition-all duration-300 text-sm whitespace-nowrap cursor-pointer w-full justify-center"
                >
                  Learn More
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
